import { useState } from 'react'
import { Info } from 'lucide-react'
import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface LastDoseTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  currentPhase?: PhaseInfo | null
  onSelect: (value: AnswerValue) => void
}

const LAST_DOSE_TIMING = [
  { id: 'within_week', label: 'Within the past week' },
  { id: '1_4_weeks', label: '1-4 weeks ago' },
  { id: '1_3_months', label: '1-3 months ago' },
  { id: '3_6_months', label: '3-6 months ago' },
  { id: 'more_6_months', label: 'More than 6 months ago' },
  { id: 'not_sure', label: "Hmm, I'm not sure" },
]

const DURATION_ON_MEDICATION = [
  { id: 'less_than_month', label: 'Less than a month' },
  { id: '1_to_3_months', label: '1-3 months' },
  { id: '3_to_6_months', label: '3-6 months' },
  { id: '6_to_12_months', label: '6-12 months' },
  { id: 'over_year', label: 'Over a year' },
  { id: 'not_sure', label: "I'm not sure" },
]

export default function LastDoseTemplate({ stage, answers, currentPhase, onSelect }: LastDoseTemplateProps) {
  const glp1Experience = answers.glp1_experience as string | undefined
  const isCurrentlyTaking = glp1Experience === 'currently_taking' || stage.id === 'ww-current-glp1-details'
  const options = isCurrentlyTaking ? DURATION_ON_MEDICATION : LAST_DOSE_TIMING

  const initialTiming = isCurrentlyTaking
    ? (answers.glp1_duration as string | undefined)
    : (answers.glp1_last_dose_timing as string | undefined)
  const [selectedTiming, setSelectedTiming] = useState(initialTiming || '')

  const handleContinue = () => {
    if (!selectedTiming) return
    if (isCurrentlyTaking) {
      onSelect({ glp1_duration: selectedTiming })
      return
    }
    onSelect({ glp1_last_dose_timing: selectedTiming })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      {!isCurrentlyTaking && (
        <div className="mb-4 flex items-start gap-3 rounded-xl bg-white/5 p-4">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-[var(--ww-accent)]" />
          <p className="text-xs leading-relaxed text-white/70">
            This is important - depending on how long it&apos;s been, I might need to ease you back in at
            a lower dose for your safety.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setSelectedTiming(option.id)}
            className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200 ${
              selectedTiming === option.id
                ? 'border-white/40 bg-white/10 text-white'
                : 'border-white/20 bg-transparent text-white/70 hover:border-white/40'
            }`}
          >
            <div
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors ${
                selectedTiming === option.id ? 'border-2 border-white' : 'border border-white/30'
              }`}
            >
              {selectedTiming === option.id && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
            </div>
            <span className="text-sm">{option.label}</span>
          </button>
        ))}
      </div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          type="button"
          disabled={!selectedTiming}
          onClick={handleContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
